import { useCallback, useEffect, useState } from 'react'

import { useFetch } from '@reveni-io/reveni-ui'

import { breakdownableConcept } from '@/returns/constants'
import {
  buildRefundConcepts,
  getConceptsWithAmount,
  getConceptsWithItemAmount,
  getConceptsWithToggledConcept,
  hasAmountErrors,
} from '@/returns/helpers/helpers'
import returnsServices from '@/returns/services/services'

import { availableRefundTypes } from '../constants'
import { getConceptsWithRejectedItemsDiscount, isProductsConceptDeselected } from './helpers'
import { transformRequestIssueRefund } from './transformers'

const noRejectedLineItemIds = []

const useManualIssueRefund = ({
  refundType,
  adjustMentRefundMethods,
  adjustmentConcepts,
  originalRefundMethod,
  refundMethods,
  refundConcepts,
  returnId,
  initialSelectedConceptsValue = false,
  rejectedLineItemsAmount = 0,
  rejectedLineItemIds = noRejectedLineItemIds,
  shippingSummaryAmount,
  lineItems,
  lineItemGroups,
}) => {
  const [concepts, setConcepts] = useState(null)
  const [refundMethod, setRefundMethod] = useState(null)
  const [note, setNote] = useState('')
  const {
    fetchData: sendIssueRefundRequest,
    loading,
    error,
  } = useFetch(
    refundType === availableRefundTypes.adjustmentRefund
      ? returnsServices.adjustmentRefunds
      : returnsServices.sendRefund,
    { doInitialRequest: false, transformRequest: transformRequestIssueRefund }
  )
  const availableConcepts = refundType === availableRefundTypes.adjustmentRefund ? adjustmentConcepts : refundConcepts
  const methods = refundType === availableRefundTypes.adjustmentRefund ? adjustMentRefundMethods : refundMethods

  const setInitValues = useCallback(() => {
    if (originalRefundMethod !== undefined) {
      setRefundMethod(originalRefundMethod)
    } else if (methods?.length) {
      setRefundMethod(methods.find(method => method.selected)?.name)
    }

    if (availableConcepts) {
      setConcepts(
        buildRefundConcepts({
          concepts: availableConcepts,
          selected: initialSelectedConceptsValue,
          shippingSummaryAmount,
          lineItems,
          lineItemGroups,
        })
      )
    }
    setNote('')
  }, [
    availableConcepts,
    initialSelectedConceptsValue,
    methods,
    shippingSummaryAmount,
    originalRefundMethod,
    lineItems,
    lineItemGroups,
  ])

  const productsMaxAmount = availableConcepts?.find(concept => concept.concept === breakdownableConcept)?.max_amount

  const applyRejectedItemsDiscount = useCallback(() => {
    setConcepts(prevConcepts => {
      return getConceptsWithRejectedItemsDiscount({
        concepts: prevConcepts,
        productsMaxAmount,
        rejectedLineItemsAmount,
        rejectedLineItemIds,
      })
    })
  }, [productsMaxAmount, rejectedLineItemsAmount, rejectedLineItemIds])

  useEffect(() => {
    if (!concepts && !refundMethod) {
      setInitValues()
    }
  }, [concepts, refundMethod, setInitValues])

  useEffect(applyRejectedItemsDiscount, [applyRejectedItemsDiscount])

  const onClickInConcept = conceptId => {
    setConcepts(prevConcepts => getConceptsWithToggledConcept(prevConcepts, conceptId))
  }

  const onChangeConceptAmount = (conceptId, amount) => {
    setConcepts(prevConcepts => getConceptsWithAmount(prevConcepts, conceptId, amount))
  }

  const onChangeItemAmount = (conceptId, itemId, amount) => {
    setConcepts(prevConcepts => getConceptsWithItemAmount(prevConcepts, conceptId, itemId, amount))
  }

  const isFormValid = () => {
    if (!refundMethod) return false
    if (!concepts?.some(concept => concept.selected)) return false
    if (!note.length) return false
    if (hasAmountErrors(concepts)) return false
    if (refundType === availableRefundTypes.refund && isProductsConceptDeselected(concepts)) return false

    return true
  }

  const onIssueRefund = async () => {
    return sendIssueRefundRequest({ returnId, refundMethod, note, concepts })
  }

  const getAvailableRefundMethods = () => {
    return refundType === availableRefundTypes.adjustmentRefund ? adjustMentRefundMethods : refundMethods
  }

  const getSelectedConcepts = () => {
    if (!concepts?.length) return []

    return concepts.filter(concept => concept.selected)
  }

  const revertChanges = () => {
    setInitValues()
  }

  return {
    refundMethod,
    setRefundMethod,
    note,
    setNote,
    concepts,
    setConcepts,
    onClickInConcept,
    onChangeConceptAmount,
    onChangeItemAmount,
    isFormValid,
    onIssueRefund,
    isIssueRefundLoading: loading,
    error,
    availableRefundMethods: getAvailableRefundMethods(),
    revertChanges,
    getSelectedConcepts,
    hasConceptAmountErrors: hasAmountErrors(concepts),
  }
}

export default useManualIssueRefund
